import React, { Component } from 'react';
import { FormikProps, FormikErrors, FormikTouched } from 'formik';
import { LabelFieldHorizontal, Hr, TextInput, Select, Checkbox, ErrorContext } from '@epicode/react-inspinia';
import { QuestionnaireElement, QuestionnaireEducationAnswerEntry } from '../../../../api/jobOffers/jobOffers.dto';
import { TFunction } from 'i18next';
import classNames from 'classnames';
import styles from './QuestionnaireEducation.module.scss';

export function renderQuestionnaireEducation<T extends Record<string, any>>(
    formikProps: FormikProps<T>,
    element: QuestionnaireElement,
    t: TFunction
) {
    const onAddClick = () => {
        const newValue = [...formikProps.values[element.id]];
        newValue.push({
            school: '',
            degree: '',
            fieldOfStudy: '',
            currentlyStudyingHere: false,
            description: '',
            startDate: '',
            startDateMonth: '',
            startDateYear: '',
            endDate: '',
            endDateMonth: '',
            endDateYear: '',
            order: newValue.length + 1
        } as QuestionnaireEducationAnswerEntry);
        formikProps.setFieldValue(element.id, newValue);
    };

    const onDeleteItem = (idx: number) => {
        const newValue = [...formikProps.values[element.id]];
        newValue.splice(idx, 1);
        formikProps.setFieldValue(element.id, newValue);
    };

    const renderItems = () => {
        return formikProps.values[element.id].map(renderItem);
    };

    const renderItem = (answer: QuestionnaireEducationAnswerEntry, idx: number) => {
        return (
            <QuestionnaireEducationItem
                key={`${element.id}.${idx}`}
                formikProps={formikProps}
                element={element}
                t={t}
                idx={idx}
                onDelete={onDeleteItem}
            />
        );
    };

    return () => (
        <div key={element.id} className='mt-4 flex-fill d-flex flex-column align-items-stretch'>
            <h4 className='font-weight-bold'>{t('jobApplication.education.title')}</h4>
            {renderItems()}
            <button
                type='button'
                className={classNames('btn text-navy btn-link align-self-start pl-0 pr-0', styles.buttonAdd)}
                onClick={onAddClick}>
                + {t('jobApplication.education.addNewButton')}
            </button>
        </div>
    );
}

export type FormValues = QuestionnaireEducationAnswerEntry & {
    startDateYear: string;
    startDateMonth: string;
    endDateYear?: string;
    endDateMonth?: string;
};

interface ItemProps<T> {
    formikProps: FormikProps<T>;
    element: QuestionnaireElement;
    t: TFunction;
    idx: number;
    onDelete: (idx: number) => void;
}

class QuestionnaireEducationItem<T extends Record<string, any>> extends Component<ItemProps<T>> {
    render() {
        const { formikProps, element, idx, t } = this.props;
        const elementName = `${element.id}.${idx}`;
        const errors = (formikProps.errors[element.id] || {}) as FormikErrors<Record<string, any>>;
        const touched = (formikProps.touched[element.id] || {}) as FormikTouched<Record<string, any>>;
        const value = formikProps.values[element.id][idx];
        const isCurrentlyStudyingChecked = value['currentlyStudyingHere'];

        return (
            <ErrorContext.Provider
                key={elementName}
                value={{
                    errors,
                    touched
                }}>
                <div className='flex-fill d-flex flex-column align-items-stretch mt-3'>
                    <LabelFieldHorizontal
                        for={`${elementName}.school`}
                        label={t('jobApplication.education.schoolLabel')}>
                        <TextInput
                            name={`${elementName}.school`}
                            onChange={formikProps.handleChange}
                            onBlur={formikProps.handleBlur}
                            value={value['school']}
                        />
                    </LabelFieldHorizontal>
                    <div className='row'>
                        <div className='col-6'>
                            <LabelFieldHorizontal
                                for={`${elementName}.degree`}
                                label={t('jobApplication.education.degreeLabel')}>
                                <TextInput
                                    name={`${elementName}.degree`}
                                    onChange={formikProps.handleChange}
                                    onBlur={formikProps.handleBlur}
                                    value={value['degree']}
                                />
                            </LabelFieldHorizontal>
                        </div>
                        <div className='col-6'>
                            <LabelFieldHorizontal
                                for={`${elementName}.fieldOfStudy`}
                                label={t('jobApplication.education.fieldOfStudyLabel')}>
                                <TextInput
                                    name={`${elementName}.fieldOfStudy`}
                                    onChange={formikProps.handleChange}
                                    onBlur={formikProps.handleBlur}
                                    value={value['fieldOfStudy']}
                                />
                            </LabelFieldHorizontal>
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-6'>
                            <LabelFieldHorizontal
                                for={{ [`${elementName}.startDateMonth`]: {}, [`${elementName}.startDateYear`]: {} }}
                                label={t('jobApplication.education.startDateLabel')}>
                                <div className='row flex-fill'>
                                    <div className='col-6'>
                                        <Select
                                            name={`${elementName}.startDateMonth`}
                                            onChange={formikProps.handleChange}
                                            onBlur={formikProps.handleBlur}
                                            value={value['startDateMonth']}>
                                            <option value=''>{t('jobApplication.education.dateMonth')}</option>
                                            {renderMonthOptions(t)}
                                        </Select>
                                    </div>
                                    <div className='col-6'>
                                        <Select
                                            name={`${elementName}.startDateYear`}
                                            onChange={formikProps.handleChange}
                                            onBlur={formikProps.handleBlur}
                                            value={value['startDateYear']}>
                                            <option value=''>{t('jobApplication.education.dateYear')}</option>
                                            {renderYearOptions()}
                                        </Select>
                                    </div>
                                </div>
                            </LabelFieldHorizontal>
                        </div>
                        <div className='col-6'>
                            {!isCurrentlyStudyingChecked && (
                                <LabelFieldHorizontal
                                    for={{ [`${elementName}.endDateMonth`]: {}, [`${elementName}.endDateYear`]: {} }}
                                    label={t('jobApplication.education.endDateLabel')}>
                                    <div className='row flex-fill'>
                                        <div className='col-6'>
                                            <Select
                                                name={`${elementName}.endDateMonth`}
                                                onChange={formikProps.handleChange}
                                                onBlur={formikProps.handleBlur}
                                                value={value['endDateMonth']}>
                                                <option value=''>{t('jobApplication.education.dateMonth')}</option>
                                                {renderMonthOptions(t)}
                                            </Select>
                                        </div>
                                        <div className='col-6'>
                                            <Select
                                                name={`${elementName}.endDateYear`}
                                                onChange={formikProps.handleChange}
                                                onBlur={formikProps.handleBlur}
                                                value={value['endDateYear']}>
                                                <option value=''>{t('jobApplication.education.dateYear')}</option>
                                                {renderYearOptions()}
                                            </Select>
                                        </div>
                                    </div>
                                </LabelFieldHorizontal>
                            )}
                        </div>
                    </div>
                    <LabelFieldHorizontal for={`${elementName}.currentlyStudyingHere`}>
                        <Checkbox
                            label={t('jobApplication.education.currentlyStudyingLabel')}
                            name={`${elementName}.currentlyStudyingHere`}
                            onChange={formikProps.handleChange}
                            onBlur={formikProps.handleBlur}
                            value={value['currentlyStudyingHere']}
                            checked={value['currentlyStudyingHere']}
                        />
                    </LabelFieldHorizontal>
                    <LabelFieldHorizontal
                        for={`${elementName}.description`}
                        label={t('jobApplication.education.descriptionLabel')}>
                        <textarea
                            className='form-control'
                            rows={4}
                            name={`${elementName}.description`}
                            onChange={formikProps.handleChange}
                            onBlur={formikProps.handleBlur}
                            value={value['description']}
                        />
                    </LabelFieldHorizontal>
                    {idx > 0 && (
                        <div className='d-flex justify-content-end'>
                            <button
                                type='button'
                                className='btn text-danger btn-link align-self-start pl-0 pr-0'
                                onClick={this.onDelete}>
                                <i className='fa fa-times' /> {t('general.button.delete')}
                            </button>
                        </div>
                    )}
                    <Hr type='solid' />
                </div>
            </ErrorContext.Provider>
        );
    }

    private onDelete = () => {
        this.props.onDelete(this.props.idx);
    };
}

const renderMonthOptions = (t: TFunction) => {
    const results = [];
    for (let month = 1; month <= 12; ++month) {
        results.push(
            <option key={`month=${month}`} value={month}>
                {t(`month.${month}`)}
            </option>
        );
    }
    return results;
};

const renderYearOptions = () => {
    const results = [];
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 50;
    for (let year = currentYear; year >= lastYear; --year) {
        results.push(
            <option key={`year-${year}`} value={year}>
                {year}
            </option>
        );
    }
    return results;
};
